// B2 commit 2: chat card for the guard-checked deal qualification proposal
// (`crm_qualification_proposal_v1`). Rendering follows the TouchStrategySnippet
// conventions (CSS variables, i18n subscription, data-testids); the approval
// footer follows the same useApprovalMutation → onDecisionApplied decision
// flow. Field labels reuse the deal-cockpit qualification keys — one
// canonical name per checklist box, never a second copy. The proposal itself
// stays read-only: writing the `Deal.qualification` block is the separate
// approval-gated update_deal_qualification execution — there is no undo for
// it, so resolved approvals render as terminal status lines.
import { useTranslation } from '@govard/i18n-core/react';
import { useI18nSubscription } from '../../../i18n/useI18nSubscription.js';
import { useState, type ReactElement } from 'react';
import { AlertTriangle, CheckCircle2, Minus } from 'lucide-react';

import { Badge, Button } from '@govard/ui-primitives';

import { useApprovalMutation } from '../../hooks/useApprovalMutation.js';
import type { ApprovalItem, ApprovalStatus } from '../../../approvals/api/approvals.client.js';
import { formatDateTime } from '../../../../shared/format/index.js';

import type { StructuredMessagePayload } from '@govard/ai-chat-kit';

export type QualificationProposalPayload = Extract<
  StructuredMessagePayload,
  { type: 'crm_qualification_proposal_v1' }
>;

/** Compact failure codes from the extract_deal_qualification tool envelope. */
export type QualificationFailureCode = 'qualification_llm' | 'qualification_no_deal';

const FAILURE_LABEL_KEYS: Readonly<Record<QualificationFailureCode, string>> = {
  qualification_llm: 'crm.chat.qualification.status_llm',
  qualification_no_deal: 'crm.chat.qualification.status_no_deal',
};

/**
 * Field labels reuse the cockpit block keys from the B2 foundation commit:
 * one canonical name per checklist box, shared by both surfaces.
 */
const FIELD_LABEL_KEYS: Record<QualificationProposalPayload['items'][number]['field'], string> = {
  need: 'crm.chat.deal_cockpit.qualification_field.need',
  budget: 'crm.chat.deal_cockpit.qualification_field.budget',
  decisionMaker: 'crm.chat.deal_cockpit.qualification_field.decisionMaker',
  decisionDeadline: 'crm.chat.deal_cockpit.qualification_field.decisionDeadline',
  selectionCriteria: 'crm.chat.deal_cockpit.qualification_field.selectionCriteria',
};

const EVIDENCE_PREVIEW_LIMIT = 120;

const APPROVAL_STATUSES: ReadonlySet<string> = new Set([
  'pending',
  'approved',
  'rejected',
  'expired',
  'cancelled',
]);

type QualificationProposalSnippetProps =
  | {
      proposal: QualificationProposalPayload;
      onDecisionApplied?: ((approval: ApprovalItem) => void) | undefined;
    }
  | { failureCode: QualificationFailureCode };

interface ResolvedDecision {
  readonly status: ApprovalStatus;
  readonly executionStatus: string | undefined;
  readonly decidedAt: string | undefined;
}

type FooterState =
  | { readonly kind: 'read_only' }
  | { readonly kind: 'pending' }
  | { readonly kind: 'executed'; readonly decidedAt: string | undefined }
  | { readonly kind: 'verifying' }
  | { readonly kind: 'failed' }
  | { readonly kind: 'declined' };

function approvalStatusOf(value: string | undefined): ApprovalStatus {
  return value !== undefined && APPROVAL_STATUSES.has(value)
    ? (value as ApprovalStatus)
    : 'pending';
}

function footerStateOf(
  proposal: QualificationProposalPayload,
  resolved: ResolvedDecision | null,
): FooterState {
  if (proposal.approvalId === undefined) return { kind: 'read_only' };
  const status = resolved?.status ?? approvalStatusOf(proposal.approvalStatus);
  if (status === 'rejected' || status === 'expired' || status === 'cancelled') {
    return { kind: 'declined' };
  }
  if (status === 'approved') {
    const executionStatus = resolved?.executionStatus ?? proposal.executionStatus;
    if (executionStatus === 'succeeded') {
      return { kind: 'executed', decidedAt: resolved?.decidedAt ?? proposal.decidedAt };
    }
    if (executionStatus === 'failed') return { kind: 'failed' };
    return { kind: 'verifying' };
  }
  return { kind: 'pending' };
}

/**
 * Tri-state box icon: confirmed facts get a check, known-missing facts a
 * warning, and "no data" a neutral dash — the cockpit convention, the icon is
 * the whole status.
 */
function QualificationStatusIcon({
  status,
}: {
  status: QualificationProposalPayload['items'][number]['status'];
}): ReactElement {
  if (status === 'confirmed') {
    return (
      <CheckCircle2 size={14} aria-hidden="true" className="mt-0.5 shrink-0 text-emerald-600" />
    );
  }
  if (status === 'missing') {
    return (
      <AlertTriangle size={14} aria-hidden="true" className="mt-0.5 shrink-0 text-amber-600" />
    );
  }
  return <Minus size={14} aria-hidden="true" className="mt-0.5 shrink-0 text-muted-foreground" />;
}

function QualificationItemRow({
  item,
}: {
  item: QualificationProposalPayload['items'][number];
}): ReactElement {
  const { t } = useTranslation();
  const [evidenceExpanded, setEvidenceExpanded] = useState(false);
  const evidence = item.evidence ?? null;
  const previewEvidence = evidence !== null && evidence.length > EVIDENCE_PREVIEW_LIMIT;
  const visibleEvidence =
    evidence === null
      ? null
      : previewEvidence && !evidenceExpanded
        ? `${evidence.slice(0, EVIDENCE_PREVIEW_LIMIT)}…`
        : evidence;

  return (
    <li
      data-testid={`qualification-item-${item.field}`}
      className="rounded-md border border-[var(--ant-color-border)] p-2.5"
    >
      <div className="flex items-start gap-2">
        <QualificationStatusIcon status={item.status} />
        <div className="min-w-0 flex-1">
          <span className="text-xs font-medium text-[var(--ant-color-text)]">
            {t(FIELD_LABEL_KEYS[item.field])}
          </span>
          {item.status === 'confirmed' && item.value != null ? (
            <p className="mt-0.5 text-xs text-[var(--ant-color-text)]">{item.value}</p>
          ) : null}
          {item.status === 'missing' ? (
            <p className="mt-0.5 text-xs text-[var(--ant-color-text-secondary)]">
              {t('crm.chat.qualification.status_missing_hint')}
            </p>
          ) : null}
        </div>
      </div>
      {visibleEvidence !== null ? (
        <div className="mt-1.5 flex flex-wrap items-baseline gap-1">
          <p
            data-testid={`qualification-evidence-${item.field}`}
            className="text-[11px] text-[var(--ant-color-text-secondary)]"
          >
            {t('crm.chat.qualification.evidence_label', { text: visibleEvidence })}
          </p>
          {previewEvidence ? (
            <button
              type="button"
              data-testid={`qualification-evidence-toggle-${item.field}`}
              className="cursor-pointer border-none bg-transparent p-0 text-[11px] font-medium text-[var(--ant-color-primary)] hover:underline"
              onClick={() => setEvidenceExpanded((current) => !current)}
            >
              {evidenceExpanded
                ? t('crm.chat.qualification.evidence_hide')
                : t('crm.chat.qualification.evidence_show')}
            </button>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}

function FailureNotice({ failureCode }: { failureCode: QualificationFailureCode }): ReactElement {
  useI18nSubscription();
  const { t } = useTranslation();
  return (
    <div
      data-testid="structured-snippet-qualification"
      className="my-2 rounded-xl border border-[var(--ant-color-error-border)] bg-[var(--ant-color-error-bg)] px-3.5 py-2.5"
    >
      <p className="text-xs text-[var(--ant-color-error-text)]">
        {t(FAILURE_LABEL_KEYS[failureCode])}
      </p>
    </div>
  );
}

export function QualificationProposalSnippet(
  props: QualificationProposalSnippetProps,
): ReactElement {
  useI18nSubscription();
  const { t } = useTranslation();
  const approvalMutation = useApprovalMutation();
  const [resolved, setResolved] = useState<ResolvedDecision | null>(null);

  if ('failureCode' in props) {
    return <FailureNotice failureCode={props.failureCode} />;
  }
  const { proposal, onDecisionApplied } = props;

  const footer = footerStateOf(proposal, resolved);
  const confirmedCount = proposal.items.filter((item) => item.status === 'confirmed').length;

  const handleDecision = async (decision: 'approve' | 'reject'): Promise<void> => {
    if (proposal.approvalId === undefined) return;
    try {
      const updatedApproval = await approvalMutation.mutateAsync({
        approvalId: proposal.approvalId,
        decision,
      });
      setResolved({
        status: updatedApproval.status,
        executionStatus: updatedApproval.executionOutcome?.status,
        decidedAt: updatedApproval.decidedAt ?? undefined,
      });
      onDecisionApplied?.(updatedApproval);
    } catch {
      // reported by useApprovalMutation onError
    }
  };

  return (
    <div
      data-testid="structured-snippet-qualification"
      className="my-2 overflow-hidden rounded-xl border border-[var(--ant-color-border)] bg-white"
    >
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[var(--ant-color-border)] px-3.5 py-2.5">
        <span className="text-xs font-semibold text-[var(--ant-color-text-heading)]">
          {t('crm.chat.qualification.title')}
        </span>
        <span className="flex items-center gap-1.5">
          <Badge variant="outline">
            {t('crm.chat.qualification.summary', {
              confirmed: confirmedCount,
              total: proposal.items.length,
            })}
          </Badge>
          {proposal.approvalId === undefined ? (
            <Badge
              variant="outline"
              className="text-[10px] font-medium uppercase tracking-wide text-[var(--ant-color-text-secondary)]"
            >
              {t('crm.chat.qualification.read_only')}
            </Badge>
          ) : null}
        </span>
      </div>

      <div className="space-y-3.5 p-3.5">
        <ul className="space-y-2">
          {proposal.items.map((item) => (
            <QualificationItemRow key={item.field} item={item} />
          ))}
        </ul>

        {footer.kind === 'pending' && proposal.approvalId !== undefined ? (
          <div className="flex flex-wrap items-center gap-2 border-t border-[var(--ant-color-border)] pt-3">
            <Button
              type="button"
              variant="primary"
              size="sm"
              data-testid="qualification-apply"
              disabled={approvalMutation.isPending}
              onClick={() => {
                void handleDecision('approve');
              }}
            >
              {t('crm.chat.qualification.apply')}
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              data-testid="qualification-decline"
              disabled={approvalMutation.isPending}
              onClick={() => {
                void handleDecision('reject');
              }}
            >
              {t('crm.chat.qualification.decline')}
            </Button>
          </div>
        ) : null}

        {footer.kind === 'executed' ? (
          <p
            data-testid="qualification-status-executed"
            className="rounded-md border border-[var(--ant-color-success-border)] bg-[var(--ant-color-success-bg)] px-3 py-2 text-xs text-[var(--ant-color-success-text)]"
          >
            {t('crm.chat.qualification.status_executed')}
            {footer.decidedAt !== undefined
              ? ` · ${t('crm.chat.qualification.executed_at', { time: formatDateTime(footer.decidedAt) })}`
              : ''}
          </p>
        ) : null}

        {footer.kind === 'verifying' ? (
          <p
            data-testid="qualification-status-pending"
            className="rounded-md bg-[var(--color-surface)] px-3 py-2 text-xs text-[var(--ant-color-text-secondary)]"
          >
            {t('crm.chat.qualification.status_pending')}
          </p>
        ) : null}

        {footer.kind === 'failed' ? (
          <p
            data-testid="qualification-status-failed"
            className="rounded-md border border-[var(--ant-color-error-border)] bg-[var(--ant-color-error-bg)] px-3 py-2 text-xs text-[var(--ant-color-error-text)]"
          >
            {t('crm.approvals.execution_failed')}
          </p>
        ) : null}

        {footer.kind === 'declined' ? (
          <p
            data-testid="qualification-status-declined"
            className="rounded-md bg-[var(--color-surface)] px-3 py-2 text-xs text-[var(--ant-color-text-secondary)]"
          >
            {t('crm.chat.qualification.status_declined')}
          </p>
        ) : null}
      </div>
    </div>
  );
}
